"use client";

import { usePathname } from "next/navigation";
import { ChevronDown } from "lucide-react";
import { type Locale } from "@/lib/i18n/config";
import { useLocale } from "@/lib/i18n/locale-context";
import { localizeHref, stripLocaleFromPath } from "@/lib/i18n/routing";
import { cn } from "@/lib/utils";

type LanguageOption = {
  locale: Locale;
  label: string;
};

export default function LanguageSwitcher({
  languages,
  className,
}: {
  languages: LanguageOption[];
  className?: string;
}) {
  const locale = useLocale();
  const pathname = usePathname();
  const pathWithoutLocale = stripLocaleFromPath(pathname);

  return (
    <div
      className={cn(
        "relative inline-grid w-fit max-w-full shrink-0 [&>*]:col-start-1 [&>*]:row-start-1",
        className,
      )}
    >
      <select
        value={locale}
        onChange={(event) => {
          const nextLocale = event.target.value as Locale;
          if (nextLocale === locale) {
            return;
          }

          // Full reload so root layout font assets and html[lang] styles apply.
          window.location.assign(localizeHref(pathWithoutLocale, nextLocale));
        }}
        aria-label="Language"
        className="col-start-1 row-start-1 h-8 w-full min-w-0 cursor-pointer select-none appearance-none rounded-md border border-line bg-background py-0 ps-2.5 pe-7 text-sm text-foreground transition-colors hover:bg-foreground/[0.04] focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-none"
      >
        {languages.map((language) => (
          <option
            key={language.locale}
            value={language.locale}
            lang={language.locale}
          >
            {language.label}
          </option>
        ))}
      </select>
      <ChevronDown
        className="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-end me-2 text-muted-foreground"
        aria-hidden
      />
    </div>
  );
}
